import { t } from '@/lib/i18n';
import { useMemberConnectorGrants, useToggleMemberGrant } from '../hooks';
import styles from './MemberConnectorsTab.module.css';

interface Props {
  memberId: string;
}

const STATUS_LABELS: Record<string, string> = {
  connected: 'Connected',
  not_connected: 'Not configured',
  error: 'Reconnect needed',
};

export function MemberConnectorsTab({ memberId }: Props) {
  const { data, isLoading } = useMemberConnectorGrants(memberId);
  const toggleGrant = useToggleMemberGrant(memberId);

  if (isLoading) return <p className={styles.loading}>{t('Loading…')}</p>;
  if (!data?.connectors.length) {
    return (
      <div className={styles.empty}>
        <p>{t('No connectors are available. Set them up in Settings → Connectors.')}</p>
      </div>
    );
  }

  return (
    <ul className={styles.list}>
      {data.connectors.map((c) => {
        const isConfigured = c.status !== 'not_connected';
        return (
          <li
            key={c.id}
            className={`${styles.row}${!isConfigured ? ` ${styles.unconfigured}` : ''}`}
          >
            <div className={styles.meta}>
              <span className={styles.name}>{c.name}</span>
              <span className={styles.status}>{STATUS_LABELS[c.status]}</span>
            </div>
            {isConfigured ? (
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={c.granted}
                  onChange={() =>
                    void toggleGrant.mutate({ connectorId: c.id, currentlyGranted: c.granted })
                  }
                  disabled={toggleGrant.isPending}
                />
                <span>{c.granted ? t('Access granted') : t('No access')}</span>
              </label>
            ) : (
              <p className={styles['unconfigured-note']}>
                {t('Set up in Settings → Connectors first')}
              </p>
            )}
          </li>
        );
      })}
    </ul>
  );
}
